import { useState, useCallback, useContext, createContext, ReactChild } from 'react';

import translationsData from 'assets/locales/translations.json'

import { useClientContext } from './useClientContext.hook';
import { ClientInterface } from '../interfaces';

export const supportedLanguages = ['en', 'es'];

export const TranslationContext = createContext({
  language: 'en' as string,
  setLanguage: (l: string): void => {},
  supportedLanguages: [] as string[],
  t: (text: string): string => '',
  translateCustomText: (customProperty: string, fallback: string): string => '',
});

export const TranslationContextProvider = (props: { children: ReactChild | ReactChild[] }) => {

  const translations: Record<typeof supportedLanguages[number], Record<string, unknown>> = translationsData;
  const [language, setLanguage]: [string, (l: string) => void] = useState('en');
  const { client } = useClientContext();

  const t = useCallback((text: string) => {
    const translation = translations[language][text] as string;
    return translation || text;
  }, [language]);

  const translateCustomText = useCallback((customProperty: string, fallback: string) => {
    const clientOriginalText = client[customProperty as keyof ClientInterface];

    if (language !== 'en' && client.translations) {
      const languageTranslations = client.translations[language];

      if (languageTranslations)
        return languageTranslations[customProperty] || clientOriginalText || fallback;
    }
    return clientOriginalText || fallback;
  }, [client, language]);

  return (
    <TranslationContext.Provider value={{ language, setLanguage, supportedLanguages, t, translateCustomText}}>
      <>{props.children}</>
    </TranslationContext.Provider>
  );
};

export const useTranslationContext = () => useContext(TranslationContext);
